/* 
  Gaussian Blur
*/
export const gaussianBlur = /* glsl */ `
  #define KERNEL_RADIUS 3
  
  vec4 gaussianBlur(sampler2D t, vec2 uv, vec2 direction, vec2 resolution, float strength, float aspect) {
    vec2 texelSize = vec2(0.00069, 0.00069);
    vec2 blurVec = direction * texelSize * strength / float(KERNEL_RADIUS);
    vec4 result = vec4(0.0);
    float weightSum = 0.0;

    float sigma = float(KERNEL_RADIUS) / 2.0;

    for (int i = -KERNEL_RADIUS; i <= KERNEL_RADIUS; i++) {
        float weight = exp(-(float(i) * float(i)) / (2.0 * sigma * sigma));
        vec4 color = texture2D(t, uv + blurVec * float(i));
        color.rgb = mix(vec3(1.0), color.rgb, color.a);
        result += color * weight;
        weightSum += weight;
    }

    return result / weightSum;
  } 
`

/* 
  Read Depth
*/
export const readDepth = /* glsl */ `
  float viewZToOrthographicDepth( const in float viewZ, const in float near, const in float far ) {
    // -near maps to 0; -far maps to 1
    return ( viewZ + near ) / ( near - far );
  }

  float perspectiveDepthToViewZ( const in float depth, const in float near, const in float far ) {
    // maps perspective depth in [ 0, 1 ] to viewZ
    return ( near * far ) / ( ( far - near ) * depth - far );
  }

  float readDepth( sampler2D depthSampler, vec2 coord ) {
    float fragCoordZ = texture2D( depthSampler, coord ).x;
    float viewZ = perspectiveDepthToViewZ( fragCoordZ, 0.1, 10. );
    return viewZToOrthographicDepth( viewZ, 6.0, 7.0); // Change here for focus point for the blur
  }
`

/* 
  Blend Overlay
*/
export const blendOverlay = /* glsl */ `
  float blendOverlay(float base, float blend) {
    return base<0.5?(2.0*base*blend):(1.0-2.0*(1.0-base)*(1.0-blend));
  }

  vec3 blendOverlay(vec3 base, vec3 blend) {
    return vec3(blendOverlay(base.r,blend.r),blendOverlay(base.g,blend.g),blendOverlay(base.b,blend.b));
  }
`

/* 
  Add
*/
export const add = /* glsl */ `
  vec4 add(vec4 a, vec4 b) {
    float newAlpha = mix(b.w, 1.0, a.w);
    vec3 newColor = mix(b.w * b.xyz, a.xyz, a.w);
    float divideFactor = (newAlpha > 0.001 ? (1.0 / newAlpha) : 1.0);
    return vec4(divideFactor * newColor, newAlpha);
  }
`

/* 
  Rotate UV
*/
export const rotateUV = /* glsl */ `
  vec2 rotateUV(vec2 uv, float angle, vec2 pivot) {
    float s = sin(angle);
    float c = cos(angle);

    // Translate UV to origin (pivot as center)
    uv -= pivot;

    // Apply 2D rotation
    uv = mat2(c, -s, s, c) * uv;

    // Translate back
    uv += pivot;

    return uv;
  }
`

/* 
  Hard Light
*/
export const hardLight = /* glsl */ `
  vec3 blendHardLight(vec3 base, vec3 blend) {
    return blendOverlay(blend,base);
  }

  vec3 blendHardLight(vec3 base, vec3 blend, float opacity) {
    return (blendHardLight(base, blend) * opacity + base * (1.0 - opacity));
  }
`
/* 
  Get UV From Grid
*/
export const getUVFromGrid = /* glsl */ `
  vec2 getUvFromGrid(vec2 uv, float index) {
    float tilesPerRow = 3.0;
    
    // Calculate column and row from the index
    float col = mod(index, tilesPerRow);
    float row = floor(index / tilesPerRow);
    
    // We need to flip the row for GLSL's texture coordinate system,
    // where (0,0) is at the bottom-left.
    float flippedRow = (tilesPerRow - 1.0) - row;
    
    // Scale and offset UVs to select the correct tile
    vec2 tileUv = vec2(
      (uv.x + col) / tilesPerRow,
      (uv.y + flippedRow) / tilesPerRow
    );
    
    return tileUv;
  }
`
